/**
 * Replay path reconstruction for horse movement.
 * Builds compressed per-horse net paths between two event indices for history jump animation.
 */

import { Coordinates } from 'app';

import { SPEED_PROFILE_HISTORY } from '~/board/animationConfig';
import { EventLogEntry, EventType } from '~/server/src/dtos';

import type { GameStateAtEvent } from './reducers';

/** Speed profile key for history animation (use with animation config). */
export const REPLAY_SPEED_PROFILE = SPEED_PROFILE_HISTORY;

/** Per-horse result: path to animate (or teleport if path empty/skipped), plus metadata. */
export interface ReplayHorsePathResult {
  horseIndex: number;
  /** Net path from start position to end (start ... waypoints ... end). Empty = no animation (teleport). */
  path: Coordinates[];
  speedProfile: string;
  sequenceId: number;
  /** Number of SELECTMOVEOPP segments in range that had no path data (data gap). */
  skippedSegments: number;
}

/**
 * Extracts move path from a SELECTMOVEOPP event payload.
 * Supports payload.move.fields, payload.fields, or payload.path (arrays of {x,y}).
 * Returns null if path details are missing (data gap).
 */
export function extractMovePathFromPayload(entry: EventLogEntry): Coordinates[] | null {
  if (entry.eventType !== EventType.SELECTMOVEOPP) return null;
  const p = entry.payload as Record<string, unknown> | undefined;
  if (!p) return null;
  const move = p.move as { fields?: Array<{ x: number; y: number }> } | undefined;
  if (move?.fields && Array.isArray(move.fields) && move.fields.length > 0) {
    const pts = move.fields.filter(
      (pt): pt is { x: number; y: number } =>
        typeof pt === 'object' &&
        pt !== null &&
        typeof (pt as { x?: number }).x === 'number' &&
        typeof (pt as { y?: number }).y === 'number',
    );
    if (pts.length > 0) return pts;
  }
  const fields = p.fields as Array<{ x: number; y: number }> | undefined;
  if (Array.isArray(fields) && fields.length > 0) {
    const pts = fields.filter(
      (pt): pt is { x: number; y: number } =>
        typeof pt === 'object' &&
        pt !== null &&
        typeof (pt as { x?: number }).x === 'number' &&
        typeof (pt as { y?: number }).y === 'number',
    );
    if (pts.length > 0) return pts;
  }
  const path = p.path as Array<{ x: number; y: number }> | undefined;
  if (Array.isArray(path) && path.length > 0) {
    const pts = path.filter(
      (pt): pt is { x: number; y: number } =>
        typeof pt === 'object' &&
        pt !== null &&
        typeof (pt as { x?: number }).x === 'number' &&
        typeof (pt as { y?: number }).y === 'number',
    );
    if (pts.length > 0) return pts;
  }
  return null;
}

/**
 * Compresses consecutive compatible movement segments (merge collinear or duplicate points).
 * Keeps path readable and reduces segment count for history skips.
 */
function compressPath(points: Coordinates[]): Coordinates[] {
  if (points.length <= 2) return points;
  const out: Coordinates[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const sameAsPrev = Math.abs(curr.x - prev.x) < 1e-6 && Math.abs(curr.y - prev.y) < 1e-6;
    const sameAsNext = Math.abs(curr.x - next.x) < 1e-6 && Math.abs(curr.y - next.y) < 1e-6;
    const collinear =
      Math.abs((curr.x - prev.x) * (next.y - curr.y) - (curr.y - prev.y) * (next.x - curr.x)) <
      1e-6;
    if (!sameAsPrev && !sameAsNext && !collinear) {
      out.push(curr);
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

/**
 * Builds compressed net movement paths per horse between two event indices.
 * Used for history jump animation: fromEventIndex = previous state, toEventIndex = target state.
 * If a SELECTMOVEOPP in range lacks path details, that segment is marked skipped (no path animation for it).
 *
 * @param eventLog - Full event log
 * @param fromEventIndex - Event index of "from" state (inclusive; horse positions at this state are used as start)
 * @param toEventIndex - Event index of "to" state (inclusive)
 * @param stateAtFrom - Game state at fromEventIndex (must have horses with field x,y)
 * @param sequenceId - Unique id for this animation intent (for cancellation)
 * @param stateAtTo - Optional game state at toEventIndex; when provided and an event has no path data,
 *                    a synthetic path [start, end] is used so we still attach animation intent (avoids 0-intent overwrite).
 * @returns Array of per-horse path results; horses that did not move in range may be omitted or have path = [currentPos]
 */
export function buildReplayHorsePaths(
  eventLog: EventLogEntry[],
  fromEventIndex: number,
  toEventIndex: number,
  stateAtFrom: GameStateAtEvent,
  sequenceId: number,
  stateAtTo?: GameStateAtEvent,
): ReplayHorsePathResult[] {
  const horses = stateAtFrom.horses;
  if (!horses || horses.length === 0) return [];

  // Clamp toEventIndex to valid range. For fromEventIndex, allow eventLog.length (live mode).
  const maxEventIndex = eventLog.length > 0 ? eventLog.length - 1 : 0;
  const clampedToEventIndex = Math.min(toEventIndex, maxEventIndex);
  // For backward nav, first event to undo is the last event index we have (when fromEventIndex >= length, use length-1)
  const backwardStartEventIndex = Math.min(fromEventIndex, maxEventIndex);

  const resultsByIndex = new Map<number, ReplayHorsePathResult>();
  const isForward = fromEventIndex <= toEventIndex;

  if (isForward) {
    // Forward navigation: iterate from fromEventIndex + 1 to toEventIndex (clamp start to valid range)
    const forwardStart = Math.min(fromEventIndex, maxEventIndex) + 1;
    for (let ei = forwardStart; ei <= clampedToEventIndex; ei++) {
      const entry = eventLog[ei];
      if (!entry || entry.eventType !== EventType.SELECTMOVEOPP) continue;

      const horseIndex = (entry.payload as Record<string, unknown> | undefined)?.horseid as
        | number
        | undefined;
      if (typeof horseIndex !== 'number' || horseIndex < 0 || horseIndex >= horses.length) continue;

      const pathFromPayload = extractMovePathFromPayload(entry);
      let result = resultsByIndex.get(horseIndex);

      if (!pathFromPayload || pathFromPayload.length === 0) {
        if (!result) {
          const field = horses[horseIndex]?.field;
          const start = field ? { x: field.x, y: field.y } : { x: 0, y: 0 };
          result = {
            horseIndex,
            path: [start],
            speedProfile: REPLAY_SPEED_PROFILE,
            sequenceId,
            skippedSegments: 0,
          };
          resultsByIndex.set(horseIndex, result);
        }
        result.skippedSegments += 1;
        continue;
      }

      if (!result) {
        const field = horses[horseIndex]?.field;
        const start = field ? { x: field.x, y: field.y } : { x: 0, y: 0 };
        result = {
          horseIndex,
          path: [start],
          speedProfile: REPLAY_SPEED_PROFILE,
          sequenceId,
          skippedSegments: 0,
        };
        resultsByIndex.set(horseIndex, result);
      }

      const currentPath = result.path;
      const last = currentPath[currentPath.length - 1];
      const first = pathFromPayload[0];
      const append =
        Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6
          ? pathFromPayload.slice(1)
          : pathFromPayload;
      result.path = compressPath([...currentPath, ...append]);
    }
  } else {
    // Backward navigation: undo events after toEventIndex, i.e. (toEventIndex, fromEventIndex]
    // If from=15 and to=14, only event 15 should be reversed.
    // When going from live to last step, undo only the last *move* (last SELECTMOVEOPP)
    const isLiveToLastStep =
      eventLog.length > 0 && fromEventIndex >= eventLog.length && toEventIndex === maxEventIndex;
    let rangeStart = backwardStartEventIndex;
    let backwardEndInclusive = clampedToEventIndex + 1;
    if (isLiveToLastStep) {
      let lastMoveEi = -1;
      for (let i = backwardStartEventIndex; i >= 0; i--) {
        if (eventLog[i]?.eventType === EventType.SELECTMOVEOPP) {
          lastMoveEi = i;
          break;
        }
      }
      if (lastMoveEi >= 0) {
        rangeStart = lastMoveEi;
        backwardEndInclusive = lastMoveEi;
      }
    } else {
      backwardEndInclusive = clampedToEventIndex + 1;
    }
    for (let ei = rangeStart; ei >= backwardEndInclusive; ei--) {
      const entry = eventLog[ei];
      if (!entry || entry.eventType !== EventType.SELECTMOVEOPP) continue;

      const horseIndex = (entry.payload as Record<string, unknown> | undefined)?.horseid as
        | number
        | undefined;
      if (typeof horseIndex !== 'number' || horseIndex < 0 || horseIndex >= horses.length) continue;

      const pathFromPayload = extractMovePathFromPayload(entry);
      let result = resultsByIndex.get(horseIndex);

      if (!pathFromPayload || pathFromPayload.length === 0) {
        const field = horses[horseIndex]?.field;
        const start = field ? { x: field.x, y: field.y } : { x: 0, y: 0 };
        // When event has no path data, use synthetic [start, end] from stateAtTo so we still
        // attach animation intent and avoid dispatching 0-intent setHorsesField (which wipes in-flight animations).
        const endField = stateAtTo?.horses?.[horseIndex]?.field;
        const end = endField ? { x: endField.x, y: endField.y } : start;
        if (!result) {
          result = {
            horseIndex,
            path: [start, end],
            speedProfile: REPLAY_SPEED_PROFILE,
            sequenceId,
            skippedSegments: 1,
          };
          resultsByIndex.set(horseIndex, result);
        } else {
          result.skippedSegments += 1;
          const last = result.path[result.path.length - 1];
          if (Math.abs(last.x - end.x) > 1e-6 || Math.abs(last.y - end.y) > 1e-6) {
            result.path = [...result.path, end];
          }
        }
        continue;
      }

      if (!result) {
        const field = horses[horseIndex]?.field;
        const start = field ? { x: field.x, y: field.y } : { x: 0, y: 0 };
        result = {
          horseIndex,
          path: [start],
          speedProfile: REPLAY_SPEED_PROFILE,
          sequenceId,
          skippedSegments: 0,
        };
        resultsByIndex.set(horseIndex, result);
      }

      // Reverse the path segment for backward navigation
      const reversedPath = [...pathFromPayload].reverse();
      const currentPath = result.path;
      const last = currentPath[currentPath.length - 1];
      const first = reversedPath[0];
      const append =
        Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6
          ? reversedPath.slice(1)
          : reversedPath;
      result.path = [...currentPath, ...append];
    }

    // For backward paths, we preserve all waypoints (even if collinear)
    // since they represent distinct movement segments from different events
    // Only remove exact duplicates
    for (const result of resultsByIndex.values()) {
      if (result.path.length > 1) {
        // Remove only exact duplicates, not collinear points
        const deduped: Coordinates[] = [result.path[0]];
        for (let i = 1; i < result.path.length; i++) {
          const prev = deduped[deduped.length - 1];
          const curr = result.path[i];
          const isDuplicate = Math.abs(curr.x - prev.x) < 1e-6 && Math.abs(curr.y - prev.y) < 1e-6;
          if (!isDuplicate) {
            deduped.push(curr);
          }
        }
        result.path = deduped;
      }
    }

    // Fallback: when no SELECTMOVEOPP in range or all paths collapsed to length 1, still attach
    // intent for horses whose position differs between from and to, so we don't dispatch 0-intent
    // setHorsesField (which wipes in-flight animations).
    if (stateAtTo?.horses && stateAtTo.horses.length === horses.length) {
      for (let i = 0; i < horses.length; i++) {
        const existing = resultsByIndex.get(i);
        if (existing && existing.path.length > 1) continue;
        const fromField = horses[i]?.field;
        const toField = stateAtTo.horses[i]?.field;
        const fromXY = fromField ? { x: fromField.x, y: fromField.y } : { x: 0, y: 0 };
        const toXY = toField ? { x: toField.x, y: toField.y } : { x: 0, y: 0 };
        if (Math.abs(fromXY.x - toXY.x) > 1e-6 || Math.abs(fromXY.y - toXY.y) > 1e-6) {
          resultsByIndex.set(i, {
            horseIndex: i,
            path: [fromXY, toXY],
            speedProfile: REPLAY_SPEED_PROFILE,
            sequenceId,
            skippedSegments: existing?.skippedSegments ?? 0,
          });
        }
      }
    }
  }

  return Array.from(resultsByIndex.values());
}
